import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Trophy, Sparkles, MapPin, CheckCircle2, Coins, Calendar, Zap, Linkedin, Share2, Copy } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicTrophyPage() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "ReferPro · Trophy Room";
    axios
      .get(`${API}/public/users/${userId}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Profile not available"));
  }, [userId]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy — long-press the URL bar instead.");
    }
  };

  const shareNative = async () => {
    if (!navigator.share) return copyLink();
    try {
      await navigator.share({
        title: `${data?.name} · ReferPro Trophy Room`,
        text: `Check out ${data?.name}'s referral wins on ReferPro.`,
        url: shareUrl,
      });
    } catch {
      // user cancelled — silent
    }
  };

  const shareLinkedin = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "noopener");
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6" data-testid="trophy-error">
        <div className="brutal bg-white p-8 max-w-md w-full text-center">
          <Trophy className="w-12 h-12 mx-auto mb-3 text-neutral-400" />
          <h1 className="font-display font-black text-2xl mb-2">Profile unavailable</h1>
          <p className="text-sm text-neutral-600">{error}</p>
          <Link to="/login" className="btn-brutal btn-primary mt-5 inline-flex">
            Back to ReferPro
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="trophy-loading">
        <div className="font-display font-black text-2xl">Loading…</div>
      </div>
    );
  }

  const initials = (data.name || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="trophy-page">
      {/* Top bar */}
      <header className="border-b-2 border-black bg-white sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="trophy-home-link">
            <div className="w-8 h-8 bg-brand-orange border-2 border-black rounded-lg flex items-center justify-center shadow-[2px_2px_0_0_#09090B]">
              <Zap className="w-4 h-4 text-white" strokeWidth={3} />
            </div>
            <span className="font-display font-black text-lg">ReferPro</span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={copyLink}
              className="btn-brutal btn-white text-xs"
              data-testid="trophy-copy-link"
            >
              <Copy className="w-3.5 h-3.5" /> Copy link
            </button>
            <button
              onClick={shareLinkedin}
              className="btn-brutal text-xs bg-[#0A66C2] text-white border-black"
              data-testid="trophy-share-linkedin"
            >
              <Linkedin className="w-3.5 h-3.5" /> Share
            </button>
            {typeof navigator !== "undefined" && navigator.share && (
              <button
                onClick={shareNative}
                className="btn-brutal btn-white text-xs"
                data-testid="trophy-share-native"
              >
                <Share2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-3xl mx-auto px-4 pt-10 pb-6">
        <div
          className="brutal p-8 relative overflow-hidden"
          style={{
            background:
              "radial-gradient(circle at 0% 0%, #FFD600 0%, transparent 50%), radial-gradient(circle at 100% 100%, #FF3AC5 0%, transparent 60%), #FAFAFA",
          }}
          data-testid="trophy-hero"
        >
          <div className="flex items-start gap-4 sm:gap-6 flex-wrap">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl border-2 border-black overflow-hidden bg-brand-blue text-white flex items-center justify-center font-display font-black text-3xl shadow-brutal flex-shrink-0">
              {data.avatar_url ? (
                <img src={data.avatar_url} alt={data.name} className="w-full h-full object-cover" />
              ) : (
                <span>{initials}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-700">Trophy room</div>
              <h1 className="font-display font-black text-3xl sm:text-5xl leading-none mt-1 break-words" data-testid="trophy-name">
                {data.name}
              </h1>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {data.team && (
                  <span className="inline-flex items-center gap-1 chip bg-white text-xs">
                    <MapPin className="w-3 h-3" /> {data.team}
                  </span>
                )}
                {data.year_joined && (
                  <span className="inline-flex items-center gap-1 chip bg-white text-xs">
                    <Calendar className="w-3 h-3" /> Joined {data.year_joined}
                  </span>
                )}
                {data.leaderboard_rank && (
                  <span className="inline-flex items-center gap-1 chip bg-brand-yellow text-xs">
                    <Trophy className="w-3 h-3" /> #{data.leaderboard_rank} on the board
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-3xl mx-auto px-4 grid grid-cols-3 gap-3 mb-6">
        <Stat label="Lifetime points" value={data.lifetime_points.toLocaleString()} sub={`= €${data.lifetime_points.toLocaleString()}`} icon={<Coins className="w-4 h-4" />} color="bg-brand-yellow" testid="trophy-stat-points" />
        <Stat label="Hires landed" value={data.total_hires} icon={<CheckCircle2 className="w-4 h-4" />} color="bg-brand-green" testid="trophy-stat-hires" />
        <Stat label="Referrals made" value={data.total_referrals} icon={<Sparkles className="w-4 h-4" />} color="bg-white" testid="trophy-stat-refs" />
      </section>

      {/* Badges */}
      <section className="max-w-3xl mx-auto px-4 pb-12">
        <div className="brutal bg-white p-5">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5" />
            <h2 className="font-display font-black text-xl">Badge wall</h2>
            <span className="chip bg-brand-yellow text-xs ml-auto">{data.badges.length}</span>
          </div>
          {data.badges.length === 0 ? (
            <p className="text-sm text-neutral-600 text-center py-6" data-testid="trophy-no-badges">
              No badges yet — but the room is ready when they unlock the first.
            </p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="trophy-badges-grid">
              {data.badges.map((b) => (
                <div
                  key={b.id}
                  className="brutal bg-white p-3 flex flex-col items-center text-center"
                  title={b.description || b.name}
                  data-testid={`trophy-badge-${b.code || b.id}`}
                >
                  <div className="w-16 h-16 rounded-xl border-2 border-black overflow-hidden bg-neutral-100 flex items-center justify-center">
                    {b.icon_url ? (
                      <img src={b.icon_url} alt={b.name} className="w-full h-full object-cover" crossOrigin="anonymous" />
                    ) : (
                      <Sparkles className="w-6 h-6 text-neutral-400" />
                    )}
                  </div>
                  <div className="mt-2 text-xs font-bold leading-tight line-clamp-2">{b.name}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-center text-xs text-neutral-500 mt-6" data-testid="trophy-footer">
          Powered by <Link to="/" className="font-bold underline">ReferPro</Link> · Refer · Earn · Recognise
        </p>
      </section>
    </div>
  );
}

function Stat({ label, value, sub, icon, color, testid }) {
  return (
    <div className={`brutal p-3 sm:p-4 ${color}`} data-testid={testid}>
      <div className="flex items-center gap-1 text-[9px] sm:text-[10px] uppercase tracking-widest font-bold opacity-80">
        {icon} <span>{label}</span>
      </div>
      <div className="font-display font-black text-2xl sm:text-3xl leading-tight mt-1">{value}</div>
      {sub && <div className="text-[10px] sm:text-xs text-neutral-700 mt-0.5">{sub}</div>}
    </div>
  );
}
